import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";

import { socketUseEffect } from "../../socket";
import PageTitle from "../core/page-title";
import SideBar from "../core/dashboards/sidebar";
import MainDashboardContent from "../core/dashboards/section";

import Dashboard from "./sub-pages/servers/dashboard";
import Activity from "./sub-pages/servers/activity";
import Tests from "./sub-pages/servers/tests";
import Uptime from "./sub-pages/servers/uptime";
import Process from "./sub-pages/servers/process";

import { useAuth } from "../../AuthContext";
import { IconEdit, IconLayoutDashboard, IconPalette, IconPlug, IconTrash } from "@tabler/icons-react";
import Servers from "./servers";
import ServersEdit from "./servers";
import ServersDelete from "./servers";

// ---- i18n ----
const dict = {
    en: {
        loading: "Loading...",
        pageTitlePrefix: "Server",
        subTitle: "Details",
        linkServers: "Servers",

        tabs: {
            dashboard: "Dashboard",
            activityPerSite: "Activity Per Site",
            latestTests: "Latest Tests",
            uptimeChecks: "Uptime Checks",
            processUsage: "Process Usage",
            edit: "Edit",
            remove: "Remove",
        },

        sort: {
            status: "Status",
            name: "Name",
            createdDate: "Created Date",
        },
    },
    ru: {
        loading: "Загрузка...",
        pageTitlePrefix: "Сервер",
        subTitle: "Детали",
        linkServers: "Серверы",

        tabs: {
            dashboard: "Панель",
            activityPerSite: "Активность по сайтам",
            latestTests: "Последние тесты",
            uptimeChecks: "Проверки аптайма",
            processUsage: "Загрузка процессов",
            edit: "Редактировать",
            remove: "Удалить",
        },

        sort: {
            status: "Статус",
            name: "Название",
            createdDate: "Дата создания",
        },
    },
    ua: {
        loading: "Завантаження...",
        pageTitlePrefix: "Сервер",
        subTitle: "Деталі",
        linkServers: "Сервери",

        tabs: {
            dashboard: "Панель",
            activityPerSite: "Активність по сайтах",
            latestTests: "Останні тести",
            uptimeChecks: "Перевірки аптайму",
            processUsage: "Завантаження процесів",
            edit: "Редагувати",
            remove: "Видалити",
        },

        sort: {
            status: "Статус",
            name: "Назва",
            createdDate: "Дата створення",
        },
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const ServerDashboard = ({ lang: langProp, ...props }) => {
    const [details, setDetails] = useState({ name: "Loading..." });
    const { id, page } = useParams();
    const [refresh, setRefresh] = useState(true);

    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const { user } = useAuth();
    const listeners = [
        { name: "servers", fn: "get", args: { user_id: user.user_id, id }, saveDataFN: setDetails, row: "0" },
    ];
    useEffect(() => socketUseEffect(listeners, user), [id, page, refresh]);

    const tabs = [
        { label: t.tabs.dashboard, page: "dashboard", link: "/server/:id/dashboard", controller: Dashboard, args: { details } },
        { label: t.tabs.processUsage, page: "processusage", link: "/server/:id/processusage", controller: Process, args: { details } },
        { label: t.tabs.edit, page: "edit", link: "/server/:id/edit", controller: ServersEdit, args: { edit: 1, id, details, refresh, setRefresh } },
        { label: t.tabs.remove, page: "delete", link: "/server/:id/delete", controller: ServersDelete, args: { delete: 1, id, details, refresh, setRefresh } },
    ];

    const icons = {
        dashboard: <IconLayoutDashboard size={20} />,
        processusage: <IconPlug size={20} />,
        edit: <IconEdit size={20} />,
        delete: <IconTrash size={20} />,
    };

    const sort = [
        { label: t.sort.status, column: "status" },
        { label: t.sort.name, column: "name" },
        { label: t.sort.createdDate, column: "created_date" },
    ];

    const titleName = details.name === "Loading..." ? t.loading : details.name;

    return (
        <>
            <PageTitle
                title={<>{t.pageTitlePrefix}: {titleName}</>}
                homeUrl={"/dashboard"}
                subTitle={t.subTitle}
                hero
                links={[{ name: t.linkServers, link: "/servers" }]}
                menuItems={tabs}
                menuIcons={icons}
                curPage={page}
                curID={id}
            />

            <div className={"card overflow-hidden invoice-application"}>
                <div className="d-flex">
                    <div className="w-25 d-none d-lg-block border-end user-chat-box">
                        <div className="app-invoice">
                            <ul className="overflow-auto invoice-users simplebar-scrollable-y" data-simplebar="init">
                                <div className="simplebar-wrapper" style={{ margin: "0px" }}>
                                    <div className="simplebar-height-auto-observer-wrapper">
                                        <div className="simplebar-height-auto-observer"></div>
                                    </div>
                                    <div className="simplebar-mask">
                                        <div className="simplebar-offset" style={{ right: "0px", bottom: "0px" }}>
                                            <div
                                                className="simplebar-content-wrapper"
                                                tabIndex="0"
                                                role="region"
                                                aria-label="scrollable content"
                                                style={{ height: "100%", overflow: "hidden scroll" }}
                                            >
                                                <div className="simplebar-content" style={{ padding: "0px" }}>
                                                    <SideBar
                                                        id={id}
                                                        sort={sort}
                                                        object={"servers"}
                                                        path={"server"}
                                                        page={page}
                                                        controller={Servers}
                                                        refresh={refresh}
                                                        setRefresh={setRefresh}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="simplebar-placeholder" style={{ width: "280px", height: "459px" }}></div>
                                </div>
                                <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
                                    <div className="simplebar-scrollbar" style={{ width: "0px", display: "none" }}></div>
                                </div>
                                <div className="simplebar-track simplebar-vertical" style={{ visibility: "visible" }}>
                                    <div
                                        className="simplebar-scrollbar"
                                        style={{ height: "25px", display: "block", transform: "translate3d(0px, 0px, 0px)" }}
                                    ></div>
                                </div>
                            </ul>
                        </div>
                    </div>
                    <div className="w-75 w-xs-100 chat-container">
                        <div className="invoice-inner-part h-100">
                            <div className="invoiceing-box">
                                <div id="custom-invoice">
                                    <MainDashboardContent tabs={tabs} page={page} id={id} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ServerDashboard;
